/**
 * oddit-header — Interactive behavior for header navigation
 * Handles: mobile drawer, desktop mega menu, search drawer, cart drawer,
 *          mobile submenus, escape key, cart count badge
 */

(function () {
  'use strict';

  const header = document.querySelector('.oddit-header');
  if (!header) return;

  // ==========================================================================
  // Mobile Drawer
  // ==========================================================================

  const menuToggle = header.querySelector('.oddit-header__menu-toggle');
  const drawer = header.querySelector('[data-drawer]');
  const drawerClose = header.querySelector('.oddit-header__drawer-close');
  const drawerOverlay = header.querySelector('[data-drawer-close]');
  let mobileMenuPreviouslyFocused = null;

  /*
   * Cache the first focus target once instead of querying the entire drawer
   * during the customer's menu interaction. Header drawer markup is static for
   * the lifetime of this section instance.
   */
  const mobileMenuFirstFocusable = drawer
    ? drawer.querySelector(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    : null;

  /*
   * Drawer geometry can be reused until scrolling/resizing makes it stale.
   * This avoids a forced layout read on the common case where the customer
   * opens the menu before the viewport/header position has changed.
   */
  let drawerPositionDirty = true;
  let lastDrawerTop = null;
  let lastDrawerHeight = null;

  // Expose the announcement bar + header stack height so sections that sit
  // under/behind it (e.g. the hero banner) can offset their content.
  const headerGroup = document.querySelector('.header-group');
  const isHomepageMobile =
    document.body.classList.contains('template-index') &&
    window.matchMedia('(max-width: 989px)').matches;

  const afterFcpCallbacks = [];
  let afterFcpWatching = false;
  let afterFcpObserver = null;

  function flushAfterFirstContentfulPaint() {
    if (afterFcpObserver) {
      afterFcpObserver.disconnect();
      afterFcpObserver = null;
    }
    afterFcpWatching = false;

    const callbacks = afterFcpCallbacks.splice(0);
    requestAnimationFrame(function () {
      callbacks.forEach(function (callback) {
        callback();
      });
    });
  }

  function runAfterFirstContentfulPaint(callback) {
    if (
      window.performance &&
      typeof window.performance.getEntriesByName === 'function' &&
      window.performance.getEntriesByName('first-contentful-paint').length
    ) {
      requestAnimationFrame(callback);
      return;
    }

    afterFcpCallbacks.push(callback);
    if (afterFcpWatching) return;
    afterFcpWatching = true;

    if ('PerformanceObserver' in window) {
      try {
        afterFcpObserver = new PerformanceObserver(function (list) {
          const hasFcp = list.getEntries().some(function (entry) {
            return entry.name === 'first-contentful-paint';
          });
          if (hasFcp) flushAfterFirstContentfulPaint();
        });
        afterFcpObserver.observe({ type: 'paint', buffered: true });
        return;
      } catch (error) {
        afterFcpObserver = null;
      }
    }

    const fallback = function () {
      flushAfterFirstContentfulPaint();
    };

    if (document.readyState === 'complete') {
      fallback();
    } else {
      window.addEventListener('load', fallback, { once: true });
    }
  }

  function setHeaderGroupHeight() {
    if (!headerGroup) return;
    document.documentElement.style.setProperty('--oddit-header-group-height', headerGroup.offsetHeight + 'px');
  }

  if (isHomepageMobile) {
    runAfterFirstContentfulPaint(setHeaderGroupHeight);
  } else {
    setHeaderGroupHeight();
  }
  window.addEventListener('resize', setHeaderGroupHeight);

  function positionDrawer(force) {
    if (!drawer) return;

    if (!force && !drawerPositionDirty) return;

    var rect = header.getBoundingClientRect();
    var top = rect.bottom;
    var height = Math.max(0, window.innerHeight - top);

    /*
     * Only write the CSS variables when the measured values actually changed.
     * This keeps the exact existing drawer position/height behavior while
     * avoiding unnecessary style invalidation.
     */
    if (top !== lastDrawerTop) {
      header.style.setProperty('--oddit-drawer-top', top + 'px');
      lastDrawerTop = top;
    }

    if (height !== lastDrawerHeight) {
      header.style.setProperty('--oddit-drawer-height', height + 'px');
      lastDrawerHeight = height;
    }

    drawerPositionDirty = false;
  }

  function openDrawer() {
    if (!drawer) return;

    positionDrawer();
    mobileMenuPreviouslyFocused = document.activeElement;

    /*
     * Commit the visible drawer state first. These are the changes the user
     * must see in the next paint.
     */
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (menuToggle) {
      menuToggle.classList.add('is-open');
      menuToggle.setAttribute('aria-label', 'Close menu');
      menuToggle.setAttribute('aria-expanded', 'true');
    }

    applyScrollColors();

    /*
     * Focus management remains intact for accessibility, but it no longer
     * competes with the first visual response to the tap. Waiting one frame is
     * imperceptible to the user and lets the drawer paint first.
     */
    if (mobileMenuFirstFocusable) {
      requestAnimationFrame(function () {
        if (
          drawer.getAttribute('aria-hidden') === 'false' &&
          mobileMenuFirstFocusable.isConnected
        ) {
          mobileMenuFirstFocusable.focus();
        }
      });
    }
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resetSubmenus();
    if (menuToggle) {
      menuToggle.classList.remove('is-open');
      menuToggle.setAttribute('aria-label', 'Open menu');
      menuToggle.setAttribute('aria-expanded', 'false');
      if (mobileMenuPreviouslyFocused && document.contains(mobileMenuPreviouslyFocused)) {
        mobileMenuPreviouslyFocused.focus();
      } else {
        menuToggle.focus();
      }
      mobileMenuPreviouslyFocused = null;
    }
    updateHeaderTransparency();
  }

  window.addEventListener('resize', function () {
    drawerPositionDirty = true;

    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      positionDrawer(true);
    }
  });

  /*
   * Mobile browser chrome can change the visual viewport height without a
   * normal window resize. Preserve the current drawer sizing in that case.
   */
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () {
      drawerPositionDirty = true;

      if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
        positionDrawer(true);
      }
    }, { passive: true });
  }

  /*
   * Warm the initial drawer geometry outside a customer interaction. If the
   * viewport/header does not move before the first menu tap, opening requires
   * no layout measurement at all.
   */
  if (isHomepageMobile) {
    /*
     * Keep the INP benefit of warmed drawer geometry, but do that layout read
     * after the first contentful paint instead of in front of it.
     */
    runAfterFirstContentfulPaint(function () {
      positionDrawer(true);
    });
  } else {
    requestAnimationFrame(function () {
      positionDrawer(true);
    });
  }

  if (menuToggle) menuToggle.addEventListener('click', function () {
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      closeDrawer();
    } else {
      openDrawer();
    }
  });
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

  // ==========================================================================
  // Desktop Mega Menu — one panel per mega_menu block, keyed by nav handle
  // ==========================================================================

  const navItems = header.querySelectorAll('.oddit-header__nav-item');
  const megamenuOverlay = header.querySelector('[data-megamenu-overlay]');
  let megamenuTimeout = null;
  let activePanel = null;

  function showMegamenuOverlay() {
    if (!megamenuOverlay) return;
    megamenuOverlay.classList.add('is-visible');
    // Position overlay below the header + mega menu after layout
    requestAnimationFrame(function () {
      var megamenu = header.querySelector('.oddit-header__megamenu[aria-hidden="false"]');
      var bottom = header.getBoundingClientRect().bottom;
      if (megamenu) {
        bottom = megamenu.getBoundingClientRect().bottom;
      }
      megamenuOverlay.style.top = bottom + 'px';
    });
  }

  function hideMegamenuOverlay() {
    if (megamenuOverlay) megamenuOverlay.classList.remove('is-visible');
  }

  function clearActiveNavLink() {
    header.querySelectorAll('.oddit-header__nav-link--megamenu-active').forEach(function (el) {
      el.classList.remove('oddit-header__nav-link--megamenu-active');
    });
  }

  function setMegamenuTriggerState(targetId, expanded) {
    var trigger = header.querySelector('[data-megamenu-target="' + targetId + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function collapseAllMegamenuTriggers() {
    header.querySelectorAll('[data-has-megamenu]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function showMegaMenu(targetId) {
    clearTimeout(megamenuTimeout);

    applyScrollColors();

    // Hide any currently visible panel and announce its trigger as collapsed
    if (activePanel && activePanel !== targetId) {
      const prev = header.querySelector('[data-megamenu="' + activePanel + '"]');
      if (prev) prev.setAttribute('aria-hidden', 'true');
      setMegamenuTriggerState(activePanel, false);
    }

    const panel = header.querySelector('[data-megamenu="' + targetId + '"]');
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'false');
    activePanel = targetId;
    setMegamenuTriggerState(targetId, true);

    // Underline the active nav link
    clearActiveNavLink();
    var activeLink = header.querySelector('[data-megamenu-target="' + targetId + '"]');
    if (activeLink) activeLink.classList.add('oddit-header__nav-link--megamenu-active');

    showMegamenuOverlay();
  }

  function hideMegaMenu() {
    clearTimeout(megamenuTimeout);
    megamenuTimeout = setTimeout(function () {
      if (activePanel) {
        const panel = header.querySelector('[data-megamenu="' + activePanel + '"]');
        if (panel) panel.setAttribute('aria-hidden', 'true');
        setMegamenuTriggerState(activePanel, false);
        activePanel = null;
      }
      clearActiveNavLink();
      hideMegamenuOverlay();
      updateHeaderTransparency();
    }, 200);
  }

  navItems.forEach(function (item) {
    const link = item.querySelector('.oddit-header__nav-link');
    if (link && link.hasAttribute('data-has-megamenu')) {
      const targetId = link.getAttribute('data-megamenu-target');

      item.addEventListener('mouseenter', function () {
        showMegaMenu(targetId);
      });

      // Keyboard: Enter, Space, or ArrowDown opens the mega menu and moves
      // focus to its first actionable link. Mouse clicks still follow the
      // top-level navigation link normally.
      link.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          showMegaMenu(targetId);
          const panel = header.querySelector('[data-megamenu="' + targetId + '"]');
          const firstLink = panel && panel.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
          if (firstLink) firstLink.focus();
        }
      });

      // Announce/open the submenu when a keyboard user tabs onto the trigger.
      link.addEventListener('focus', function () {
        showMegaMenu(targetId);
      });

      // Don't hide on mouseleave from the nav item — let the header-level
      // mouseleave handle closing so the user can move to the mega menu freely
    } else {
      item.addEventListener('mouseenter', hideMegaMenu);
    }
  });

  // Keep mega menu open while hovering anywhere in the header (nav + mega menu panel)
  header.addEventListener('mouseleave', function () {
    if (activePanel) hideMegaMenu();
  });

  // Cancel any pending hide when re-entering the header
  header.addEventListener('mouseenter', function () {
    if (activePanel) clearTimeout(megamenuTimeout);
  });

  // Close mega menu when hovering or clicking the overlay
  if (megamenuOverlay) {
    function closeMegaMenuImmediate() {
      clearTimeout(megamenuTimeout);
      if (activePanel) {
        var panel = header.querySelector('[data-megamenu="' + activePanel + '"]');
        if (panel) panel.setAttribute('aria-hidden', 'true');
        setMegamenuTriggerState(activePanel, false);
        activePanel = null;
      }
      clearActiveNavLink();
      hideMegamenuOverlay();
      updateHeaderTransparency();
    }
    megamenuOverlay.addEventListener('mouseenter', closeMegaMenuImmediate);
    megamenuOverlay.addEventListener('click', closeMegaMenuImmediate);
  }

  // ==========================================================================
  // Search Drawer Toggle (desktop + mobile)
  // ==========================================================================

  header.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (window.odditSearchDrawer) window.odditSearchDrawer.open();
    });
  });

  var desktopSearchInput = header.querySelector('.oddit-header__search-input');
  if (desktopSearchInput) {
    desktopSearchInput.addEventListener('focus', function () {
      // Do not reopen the drawer when focus is being restored after it closes.
      if (window.odditSearchDrawerRestoringFocus) return;
      if (window.odditSearchDrawer) window.odditSearchDrawer.open();
    });
  }

  // ==========================================================================
  // Cart Drawer Toggle (desktop + mobile)
  // ==========================================================================

  header.querySelectorAll('[data-cart-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (window.odditCartDrawer) window.odditCartDrawer.open();
    });
  });

  // ==========================================================================
  // Cart Count Badge — listens for cart:updated custom events
  // ==========================================================================

  document.addEventListener('cart:updated', function (e) {
    const cart = e.detail && e.detail.cart;
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? '' : 'none';
    });
  });

  // ==========================================================================
  // Mobile Drawer Submenus (second-level navigation)
  // ==========================================================================

  const drawerLevels = header.querySelector('[data-drawer-levels]');

  function openSubmenu(target) {
    if (!drawerLevels) return;
    drawerLevels.querySelectorAll('[data-submenu]').forEach(function (sm) {
      sm.classList.remove('is-active');
    });
    const targetSubmenu = drawerLevels.querySelector('[data-submenu="' + target + '"]');
    if (targetSubmenu) {
      targetSubmenu.classList.add('is-active');
      drawerLevels.classList.add('oddit-header__drawer-levels--submenu-open');
      const panel = header.querySelector('.oddit-header__drawer-panel');
      if (panel) panel.scrollTop = 0;
    }
  }

  function closeSubmenu() {
    if (!drawerLevels) return;
    drawerLevels.classList.remove('oddit-header__drawer-levels--submenu-open');
    setTimeout(function () {
      drawerLevels.querySelectorAll('[data-submenu]').forEach(function (sm) {
        sm.classList.remove('is-active');
      });
    }, 300);
  }

  function resetSubmenus() {
    if (!drawerLevels) return;
    drawerLevels.classList.remove('oddit-header__drawer-levels--submenu-open');
    drawerLevels.querySelectorAll('[data-submenu]').forEach(function (sm) {
      sm.classList.remove('is-active');
    });
  }

  header.querySelectorAll('.oddit-header__drawer-link[data-submenu-target]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      const target = trigger.getAttribute('data-submenu-target');
      if (target) openSubmenu(target);
    });
  });

  header.querySelectorAll('[data-submenu-back]').forEach(function (btn) {
    btn.addEventListener('click', closeSubmenu);
  });

  // ==========================================================================
  // Escape key — closes drawer and any open mega menu panel
  // ==========================================================================

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' && drawer && drawer.getAttribute('aria-hidden') === 'false') {
      const focusable = drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      return;
    }
    if (e.key !== 'Escape') return;
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      closeDrawer();
    }
    if (activePanel) {
      const closingPanel = activePanel;
      const panel = header.querySelector('[data-megamenu="' + closingPanel + '"]');
      if (panel) panel.setAttribute('aria-hidden', 'true');
      setMegamenuTriggerState(closingPanel, false);
      activePanel = null;
      clearTimeout(megamenuTimeout);
      clearActiveNavLink();
      hideMegamenuOverlay();
      const trigger = header.querySelector('[data-megamenu-target="' + closingPanel + '"]');
      if (trigger) trigger.focus();
    }
  });

  // ==========================================================================
  // Header color management — default & scrolled states + transparent (homepage)
  // ==========================================================================

  var scrollBg = header.getAttribute('data-scroll-bg') || '#ffffff';
  var scrollText = header.getAttribute('data-scroll-text') || '#000000';
  var transparentText = header.getAttribute('data-transparent-text') || '#ffffff';
  var transparentEnabled = header.getAttribute('data-transparent-enabled') === 'true';
  var isHomepage = document.body.classList.contains('template-index');

  function isLightColor(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
  }

  // The header only overlays the first section when that section is a
  // transparent hero (which is built to sit full-bleed behind the fixed nav).
  // For any other first section — a banner, a non-hero section, or no hero at
  // all — return null so the header falls back to its opaque, in-flow (sticky)
  // state and never overlaps the content (ELA-186).
  function getOverlayHeroStyle() {
    var main = document.getElementById('MainContent');
    var firstSection = main && main.querySelector('.shopify-section');
    var hero = firstSection && firstSection.querySelector('.oddit-section-hero[data-header-style]');
    if (!hero) return null;
    var style = hero.getAttribute('data-header-style');
    if (style === 'transparent_white' || style === 'transparent_black') return style;
    return null;
  }

  function applyHeaderStyle() {
    var overlay = transparentEnabled && getOverlayHeroStyle() !== null;
    document.body.classList.toggle('oddit-header-opaque', !overlay);
  }

  // Remember the visual state so scroll events do not keep writing the same
  // inline styles/classes and invalidating style calculation over and over.
  var headerVisualState = null;

  function applyDefaultColors() {
    if (headerVisualState === 'default') return;
    headerVisualState = 'default';
    header.style.removeProperty('--oddit-header-bg');
    header.style.removeProperty('--oddit-header-text');
    header.classList.remove('oddit-header--transparent');
    header.classList.remove('oddit-header--invert-logo');
  }

  function applyScrollColors() {
    if (headerVisualState === 'scrolled') return;
    headerVisualState = 'scrolled';
    header.style.setProperty('--oddit-header-bg', scrollBg);
    header.style.setProperty('--oddit-header-text', scrollText);
    header.classList.remove('oddit-header--transparent');
    header.classList.remove('oddit-header--invert-logo');
  }

  function applyTransparent() {
    if (headerVisualState === 'transparent') return;
    headerVisualState = 'transparent';
    header.style.setProperty('--oddit-header-text', transparentText);
    header.style.removeProperty('--oddit-header-bg');
    header.classList.add('oddit-header--transparent');
    if (isLightColor(transparentText)) {
      header.classList.add('oddit-header--invert-logo');
    } else {
      header.classList.remove('oddit-header--invert-logo');
    }
  }

  var updateHeaderTransparency = function () {
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') return;

    if (window.scrollY > 10) {
      applyScrollColors();
    } else if (isHomepage && transparentEnabled && getOverlayHeroStyle() !== null) {
      applyTransparent();
    } else {
      applyDefaultColors();
    }
  };

  // Scroll can fire many times per frame. Coalesce it into a single visual
  // update per animation frame; the state guard above then makes unchanged
  // frames effectively free.
  var headerScrollTicking = false;
  function handleHeaderScroll() {
    /*
     * Scrolling can move the sticky header relative to an announcement bar.
     * Mark cached drawer geometry stale, but do not measure anything here.
     */
    drawerPositionDirty = true;

    if (headerScrollTicking) return;
    headerScrollTicking = true;
    requestAnimationFrame(function () {
      headerScrollTicking = false;
      updateHeaderTransparency();
    });
  }

  if (isHomepage) applyHeaderStyle();
  updateHeaderTransparency();
  window.addEventListener('scroll', handleHeaderScroll, { passive: true });

  if (isHomepage) {
    // Re-evaluate on any section add / remove / reorder in the theme editor so
    // the overlay vs. opaque state always tracks whatever section is first.
    function reapplyHeaderState() {
      applyHeaderStyle();
      updateHeaderTransparency();
    }
    document.addEventListener('shopify:section:load', reapplyHeaderState);
    document.addEventListener('shopify:section:unload', reapplyHeaderState);
  }
})();
